import { RING_MATERIALS } from "./RingMaterials";
import { IRingData, IMaterialGroup, ISurfaceGroup, IBackendStoneGroup, IMetalGroup, IStoneGroup, ISizeExceptions, IException, ICopyExceptionAction, IMinMaxThickness, IIdealThickness, IRotation, IPreventExceptionAction, IMessageExceptionAction } from "../sharedInterfaces";
import { RenderEngine } from "../Controller/3DController/core/RenderEngine";
import { IFingerSize, FINGER_SIZES } from "./RingSizes";
import { IStoneCut, STONE_CUTS } from "./RingStoneCuts";

/**
 * Holds static data for a given ring, regardless of whether or not it is currently active.
 */

export interface IMetalGroupExt extends IMetalGroup {
  materialZones:number[],
  surfaceZones:number[],
  disabledMaterialOptions:('Yellow Gold'|'White Gold'|'Rose Gold'|'Platinum'|'Palladium'|'Sterling Silver'|'Titanium'|'Zirconium Grey'|'Zirconium Black'|'Tantalum'|'Carbon Fiber')[],
  disabledSurfaceOptions:('Polished'|'Brushed'|'Side Brushed'|'Hammered'|'Ice Matte'|'Diagonal Matte'|'Sand Matte')[],
  disabledPurityOptions:('9ct'|'14ct'|'18ct'|'500'|'600'|'925'|'950')[],
  hideFromMenu:boolean
}

export interface IMetalZone {
  id:number,
  name?:string,
  material:'Yellow Gold'|'White Gold'|'Rose Gold'|'Platinum'|'Palladium'|'Sterling Silver'|'Titanium'|'Zirconium Grey'|'Zirconium Black'|'Tantalum'|'Carbon Fiber',
  surface:'Polished'|'Brushed'|'Side Brushed'|'Hammered'|'Ice Matte'|'Diagonal Matte'|'Sand Matte',
  purity:'9ct'|'14ct'|'18ct'|'500'|'600'|'925'|'950'
}

export interface IStoneGroupExt extends IStoneGroup {
  zones:number[],
  hideFromMenu:boolean
}

export interface IStoneZone {
  id:number,
  name:string,
  stone:'White Diamond'|'Black Diamond'|'Sapphire'|'Ruby'
}

export interface IMorphOptions {
  width:number[],
  thickness:number[],
  stoneSize:number[]
}

export class RingModel {
  private _SKU:string;
  private _stoneCount:number;
  private _fingerSizeOptions:IFingerSize[];
  private _stoneCut:IStoneCut;
  private _defaultWidth:number;
  private _defaultThickness:number;
  private _defaultStoneSize:number;
  private _minMaxThickness:IMinMaxThickness[];
  private _idealThickness:IIdealThickness[];
  private _rotation:IRotation;
  private _metalGroups:IMetalGroupExt[];
  private _metalZones:IMetalZone[];
  private _stoneGroups:IStoneGroupExt[];
  private _stoneZones:IStoneZone[];
  private _meshes:THREE.Mesh[];
  private _morphOptions:IMorphOptions;
  private _sizeExceptions:ISizeExceptions;
  private _materialExceptions:IException[];
  private _surfaceExceptions:IException[];
  private _circumference:number;
  private _widthToEngravingZoneFactor:number;

  public get SKU():string { return this._SKU };
  public get stoneCount():number { return this._stoneCount };
  public get fingerSizeOptions():IFingerSize[] {
    const fingerSizeOptions = this._fingerSizeOptions.map((fso) => {
      const fingerSizeOption:IFingerSize = {
        mm: fso.mm,
        auuk: fso.auuk,
        usca: fso.usca,
        eu: fso.eu,
        ch: fso.ch,
        jpc: fso.jpc
      }

      return fingerSizeOption;
    });
    
    return fingerSizeOptions;
  };
  public get stoneCut():IStoneCut {
    return {
      id: this._stoneCut.id,
      diameterToDepth: this._stoneCut.diameterToDepth
    }
  };
  public get defaultWidth():number { return this._defaultWidth };
  public get defaultThickness():number { return this._defaultThickness };
  public get defaultStoneSize():number { return this._defaultStoneSize };
  public get rotation():IRotation {
    return {
      x: this._rotation.x
    }
  };
  public get metalGroups():IMetalGroupExt[] {  //Deep copy...
    const metalGroups = this._metalGroups.map((mg) => {
      const metalGroup:IMetalGroupExt = {
        name: mg.name,
        material: mg.material,
        materialOptions: mg.materialOptions.slice(0),
        disabledMaterialOptions: mg.disabledMaterialOptions.slice(0),
        surface: mg.surface,
        surfaceOptions: mg.surfaceOptions.slice(0),
        disabledSurfaceOptions: mg.disabledSurfaceOptions.slice(0),
        purity: mg.purity,
        purityOptions: mg.purityOptions.slice(0),
        disabledPurityOptions: mg.disabledPurityOptions.slice(0),
        materialZones: mg.materialZones.slice(0),
        surfaceZones: mg.surfaceZones.slice(0),
        hideFromMenu: mg.hideFromMenu
      }

      return metalGroup;
    })

    return metalGroups;
  };// /get metalGroups()
  public get metalZones():IMetalZone[] { //Deep copy...
    const metalZones = this._metalZones.slice(0);
    return metalZones;
  };// /get metalZones()
  public get stoneGroups():IStoneGroupExt[] {  //Deep copy...
    const stoneGroups = this._stoneGroups.map((sg) => {
      const stoneGroup:IStoneGroupExt = {
        name: sg.name,
        zones: sg.zones.slice(0),
        stone: sg.stone,
        stoneOptions: sg.stoneOptions.slice(0),
        hideFromMenu: sg.hideFromMenu
      }

      return stoneGroup;
    })

    return stoneGroups;
  };// /get stoneGroups()
  public get stoneZones():IStoneZone[] {  //Deep copy...
    const stoneZones = this._stoneZones.slice(0);
    return stoneZones;
  };// /get stoneZones()
  public get morphOptions():IMorphOptions { //Deep copy...
    const mo = this._morphOptions;

    const morphOptions:IMorphOptions = {
      width: mo.width.slice(0),
      thickness: mo.thickness.slice(0),
      stoneSize: mo.stoneSize.slice(0)
    }
    
    return morphOptions;
  };// /get morphOptions()
  public get materialExceptions():IException[] {  //FIXME: Deep copy?
    return this._materialExceptions;
  };// /get materialExceptions()
  public get surfaceExceptions():IException[] {  //FIXME: Deep copy?
    return this._surfaceExceptions;
  };// /get surfaceExceptions()
  public get circumference():number {
    return this._circumference;
  };// /get circumference()
  public get ringWidthToEngravingZoneHeightFactor():number {
    return this._widthToEngravingZoneFactor;
  };// /get widthToEngravingZoneFactor()

  public set circumference(setTo:number) {
    this._circumference = setTo;
  };// /set circumference()
  public set ringWidthToEngravingZoneHeightFactor(setTo:number) {
    this._widthToEngravingZoneFactor = setTo;
  };// /set widthToEngravingZoneFactor()


  constructor(ringMetadata:IRingData) {
    this._SKU = ringMetadata.SKU;
    this._stoneCount = ringMetadata.stoneCount;
    this._stoneCut = this._getStoneCut(ringMetadata);
    this._defaultWidth = ringMetadata.defaultWidth;
    this._defaultThickness = ringMetadata.defaultThickness;
    this._defaultStoneSize = ringMetadata.defaultStoneSize;
    this._fingerSizeOptions = this._getFingerSizeOptions(ringMetadata);
    this._minMaxThickness = this._copyMinMaxThickness(ringMetadata);
    this._idealThickness = this._copyIdealThickness(ringMetadata);
    this._rotation = this._copyRotation(ringMetadata);
    this._metalGroups = this._generateMetalGroups(ringMetadata);
    this._metalZones = this._generateMetalZones(this._metalGroups);
    this._stoneGroups = this._generateStoneGroups(ringMetadata);
    this._stoneZones = this._generateStoneZones(this._stoneGroups);
    this._morphOptions = this._getMorphOptions(ringMetadata);
    this._sizeExceptions = this._copySizeExceptions(ringMetadata);
    this._materialExceptions = this._copyExceptions(ringMetadata.materialExceptions, 'material');
    this._surfaceExceptions = this._copyExceptions(ringMetadata.surfaceExceptions, 'surface');
    this._circumference = null;
  };// /constructor()

  //We can't set initialize some constants in the construstor, since we need to have initialized renderEngine to get them which in turn needs an array of RingModels, so...
  public initialize = (renderEngine:RenderEngine):Promise<void> => {
    return new Promise ((resolve, reject) => {
      renderEngine.engine.services.render.getAllMeshesOfCurrentScene()
      .then((meshes:THREE.Mesh[]) => {
        // console.log(meshes);

        meshes = meshes.filter((mesh) => (mesh.userData['ringSKU'] === this.SKU));

        if (meshes.length < 1) {
          throw new Error(`No child meshes found for ${this._SKU}`);
        };

        this._meshes = meshes;
        //console.log(`Meshes of ${this._SKU}`, this._meshes);

        this._assignMorphOptionIndexes();

        resolve();
      });
    });
  };// /initialize()

  private _getStoneCut = (ringMetadata:IRingData):IStoneCut => {
    const stoneCut = STONE_CUTS.find(sc => sc.id === ringMetadata.stoneCut);
    return stoneCut;
  }// /_getStoneCut()

  private _getFingerSizeOptions = (ringMetadata:IRingData):IFingerSize[] => {
    const fingerSizeOptions = FINGER_SIZES.filter((rs) => ringMetadata.sizes.includes(rs.auuk));
    return fingerSizeOptions;
  };// /_getFingerSizeOptions()

  private _copyMinMaxThickness = (ringMetadata:IRingData):IMinMaxThickness[] => {
    const minMaxThickness = ringMetadata.minMaxThickness.map((mmt) => {
      const minMaxThicknessEntry = {
        width: mmt.width,
        min: mmt.min,
        max: mmt.max
      };
      return minMaxThicknessEntry;
    });
    return minMaxThickness;
  };// /_copyMinMaxThickness()

  private _copyIdealThickness = (ringMetadata:IRingData):IIdealThickness[] => {
    const idealThickness = ringMetadata.idealThickness.map((it) => {
      const idealThicknessEntry = {
        width: it.width,
        ideal: it.ideal
      }
      return idealThicknessEntry;
    });
    return idealThickness;
  };// /_copyIdealThickness()

  private _copyRotation = (ringMetadata:IRingData):IRotation => {
    const rotation = {
      x: ringMetadata.rotation.x
    };
    return rotation;
  };// /_copyRotation()

  private _generateMetalGroups = (ringMetadata:IRingData):IMetalGroupExt[] => {
    const metalGroups:IMetalGroupExt[] = [];

    const generateMetalGroup = (name:string, materialGroup:IMaterialGroup, surfaceGroup:ISurfaceGroup):IMetalGroupExt => {
      //If materialGroup or surfaceGroup doesn't exist we fall back on default values
      if (!materialGroup) {
        materialGroup = {
          name: null,
          zones: [],
          materialOptions: [],
          defaultMaterial: null,
          hideFromMenu: false
        }
      }

      if (!surfaceGroup) {
        surfaceGroup = {
          name: null,
          zones: [],
          surfaceOptions: ['Polished'],
          defaultSurface: 'Polished',
          hideFromMenu: false
        }
      }

      const metalGroup:IMetalGroupExt = {
        name: name,
        material: null,
        materialOptions: [],
        disabledMaterialOptions: [],
        surface: null,
        surfaceOptions: [],
        disabledSurfaceOptions: [],
        purity: null,
        purityOptions: [],
        disabledPurityOptions: [],
        materialZones: [],
        surfaceZones: [],
        hideFromMenu: false
      }

      metalGroup.material = materialGroup.defaultMaterial;
      metalGroup.materialOptions = materialGroup.materialOptions.slice(0);

      metalGroup.surface = surfaceGroup.defaultSurface;
      metalGroup.surfaceOptions = surfaceGroup.surfaceOptions.slice(0);

      const ringMaterial = RING_MATERIALS.find(rm => rm.name === metalGroup.material);
      if (ringMaterial) {
        metalGroup.purityOptions = ringMaterial.purities.slice(0);
        metalGroup.purity = metalGroup.purityOptions[0] || null;
      }
      
      //console.log(materialGroup, surfaceGroup);

      metalGroup.materialZones = materialGroup.zones.slice(0);
      metalGroup.surfaceZones = surfaceGroup.zones.slice(0);

      metalGroup.hideFromMenu = (materialGroup.hideFromMenu || surfaceGroup.hideFromMenu) === true;

      return metalGroup;
    }// /const generateMetalGroup()

    //Generate metal groups by looping over material groups
    ringMetadata.materialGroups.forEach((matGroup) => {
      const surfGroup = ringMetadata.surfaceGroups.find(sg => sg.name === matGroup.name); //corresponding surface group
      metalGroups.push( generateMetalGroup(matGroup.name, matGroup, surfGroup) );
    })

    //Now loop over surface groups and generate missing metal groups
    ringMetadata.surfaceGroups.forEach((surfGroup) => {
      const alreadyExists = metalGroups.find(mg => mg.name === surfGroup.name);

      if (!alreadyExists) {
        const matGroup = ringMetadata.materialGroups.find(mg => mg.name === surfGroup.name); //corresponding metal group
        metalGroups.push( generateMetalGroup(surfGroup.name, matGroup, surfGroup) );
      }
    });

    return metalGroups;
  }// /_generateMetalGroups()

  private _generateMetalZones = (metalGroups:IMetalGroupExt[]):IMetalZone[] => {
    const metalZones:IMetalZone[] = [];

    const generateMetalZone = (id:number, defaultMaterial:any, defaultPurity:any, defaultSurface:any):IMetalZone => {
      const metalZone:IMetalZone = {
        id: id,
        name: this.SKU + '_Zone_' + id.toString().padStart(2, '0'),
        material: defaultMaterial,
        surface: defaultSurface,
        purity: defaultPurity
      }

      return metalZone;
    }// /const generateMetalZone()

    metalGroups.forEach((metalGroup) => {
      //Create metalZones based on materialZones
      metalGroup.materialZones.forEach((materialZone) => {
        const alreadyExists = metalZones.find(mz => mz.id === materialZone);

        if (!alreadyExists) {
          const metalZone = generateMetalZone(materialZone, metalGroup.material, metalGroup.purity, metalGroup.surface);
          metalZones.push(metalZone);
        } else {
          console.error('Duplicate metalZone!', materialZone, this.SKU);
        }
      });

      //Add default surfaces based on surfaceZones
      metalGroup.surfaceZones.forEach((surfaceZone) => {
        const found = metalZones.find(mz => mz.id === surfaceZone);
        if (found) found.surface = metalGroup.surface;
      });
    })

    return metalZones;
  }// /_generateMetalZones()

  private _generateStoneGroups = (ringMetadata:IRingData):IStoneGroupExt[] => {
    const stoneGroups:IStoneGroupExt[] = [];

    const generateStoneGroup = (sourceStoneGroup:IBackendStoneGroup):IStoneGroupExt => {
      const stoneGroup:IStoneGroupExt = {
        name: sourceStoneGroup.name,
        zones: sourceStoneGroup.zones.slice(0),
        stone: sourceStoneGroup.defaultType,
        stoneOptions: sourceStoneGroup.typeOptions.slice(0),
        hideFromMenu: sourceStoneGroup.hideFromMenu || false
      };

      return stoneGroup;
    }// /const generateStoneGroup()

    ringMetadata.stoneGroups.forEach((stnGroup) => {
      stoneGroups.push( generateStoneGroup(stnGroup) );
    });

    return stoneGroups;
  };// /_generateStoneGroups()

  private _generateStoneZones = (stoneGroups:IStoneGroupExt[]):IStoneZone[] => {
    const stoneZones:IStoneZone[] = [];

    const generateStoneZone = (id:number, defaultStone:any):IStoneZone => {
      const stoneZone:IStoneZone = {
        id: id,
        name: this.SKU + '_Stone_' + id.toString().padStart(2, '0'),
        stone: defaultStone
      }

      return stoneZone;
    }// /const generateStoneZone()

    stoneGroups.forEach((stoneGroup) => {
      stoneGroup.zones.forEach((zone) => {
        const alreadyExists = stoneZones.find(sz => sz.id === zone);

        if (!alreadyExists) {
          const stoneZone = generateStoneZone(zone, stoneGroup.stone);
          stoneZones.push(stoneZone);
        } else {
          console.error('Duplicate stoneZone!', zone);
        }
      });
    });

    return stoneZones;
  };// /_generateStoneZones()

  private _getMorphOptions = (ringMetadata:IRingData) => {
    const morphOptions:IMorphOptions = {
      width: ringMetadata.widthOptions.sort((a, b) => {return a - b}),
      thickness: ringMetadata.thicknessOptions.sort((a, b) => {return a - b}),
      stoneSize: ringMetadata.stoneSizeOptions.sort((a, b) => {return a - b})
    };

    return morphOptions;
  };// /_getMorphOptions()

  /* Store indexes of mesh.morphTargetInfluences by name so we don't need to traverse mesh.morphTargetDictionary every time we need to access them.
  /  Also avoid case-sensitivity issues and issues with misnamed stoneSize morphTargets. */ 
  private _assignMorphOptionIndexes = () => {
    const handleSingleMesh = (mesh:THREE.Mesh) => {
      const morphTargetDictionary = mesh.morphTargetDictionary;
      mesh.userData.morphIndexes = {
        width: null,
        thickness: null,
        stoneSize: null
      };
      if (!morphTargetDictionary) return;

      for (let prop in morphTargetDictionary) {
        switch (prop.toLowerCase()) {
          case 'width':
            mesh.userData.morphIndexes.width = morphTargetDictionary[prop];
            break;
          case 'thickness':
            mesh.userData.morphIndexes.thickness = morphTargetDictionary[prop];
            break;
          case 'stone':
          case 'stonesize':
          case 'stones':
          case 'stonessize':
            mesh.userData.morphIndexes.stoneSize = morphTargetDictionary[prop];
            break;
          default:
            console.error("Unknown morphTarget!", prop, mesh);
        };
      }
    };// /const handleSingleMesh()

    this._meshes.forEach((mesh) => {
      handleSingleMesh(mesh);
    });
  };// /_assignMorphOptionIndexes()

  private _copyExceptions = (sourceExceptions:IException[] = [], type:'material'|'surface') => {
    const exceptions = [];

    sourceExceptions.forEach((sourceException) => {
      const exception:IException = {
        conditions: [],
        actions: [],
        type: type
      };

      sourceException.conditions.forEach((sc) => {
        exception.conditions.push({
          group: sc.group,
          option: sc.option.splice(0),
          wasChanged: sc.wasChanged || false
        });
      });

      const handleCopyAction = (sourceAction:ICopyExceptionAction) => {
        const action:ICopyExceptionAction = {
          type: 'copy',
          from: sourceAction.from,
          to: sourceAction.to.splice(0)
        };
        exception.actions.push(action);
      };// /const handleCopyAction()

      const handlePreventAction = (sourceAction:IPreventExceptionAction) => {
        const action:IPreventExceptionAction = {
          type: 'prevent',
          group: sourceAction.group.splice(0),
          option: sourceAction.option.splice(0)
        };
        exception.actions.push(action);
      };// /const handlePreventAction()

      const handleMessageAction = (sourceAction:IMessageExceptionAction) => {
        const action:IMessageExceptionAction = {
          type: 'message',
          message: sourceAction.message
        };
        exception.actions.push(action);
      };// /const handlePreventAction()

      sourceException.actions.forEach((sa) => {
        switch (sa.type) {
          case 'copy':
            handleCopyAction(sa as ICopyExceptionAction);
            break;
          case 'prevent':
            handlePreventAction(sa as IPreventExceptionAction);
            break;
          case 'message':
            handleMessageAction(sa as IMessageExceptionAction);
            break;
        }
      });

      exceptions.push(exception);
    });

    return exceptions;
  };// /_copyMaterialExceptions()

  private _copySizeExceptions = (ringMetadata:IRingData):ISizeExceptions => {
    const sizeExceptions:ISizeExceptions = {
      maxStoneSize: [],
      maxStoneDepth: []
    }

    if (ringMetadata.sizeExceptions) {
      if (ringMetadata.sizeExceptions.maxStoneSize) {
        sizeExceptions.maxStoneSize = ringMetadata.sizeExceptions.maxStoneSize.splice(0);
      }

      if (ringMetadata.sizeExceptions.maxStoneDepth) {
        sizeExceptions.maxStoneDepth = ringMetadata.sizeExceptions.maxStoneDepth.splice(0);
      }
    }

    return sizeExceptions;
  };// /_copySizeExceptions()

  public getMaxStoneSize = (width:number, thickness:number) => {
    let maxStoneSize = Infinity;

    this._sizeExceptions.maxStoneSize.forEach((sizeException) => {
      sizeException = sizeException.replace('[WIDTH]', width.toString());
      sizeException = sizeException.replace('[THICKNESS]', thickness.toString());
      const possibleMax = eval(sizeException);

      maxStoneSize = Math.min(maxStoneSize, possibleMax);
    });

    this._sizeExceptions.maxStoneDepth.forEach((sizeException) => {
      sizeException = sizeException.replace('[WIDTH]', width.toString());
      sizeException = sizeException.replace('[THICKNESS]', thickness.toString());
      const possibleMax = eval(sizeException)/ this._stoneCut.diameterToDepth;

      maxStoneSize = Math.min(maxStoneSize, possibleMax);
    });

    //console.log("getMaxStoneSize!", maxStoneSize);

    return maxStoneSize;
  };// /getMaxStoneSize()

  public getMinMaxThickness = (width:number):IMinMaxThickness => {
    const minMaxThicknessEntry = this._minMaxThickness.find(mmt => mmt.width === width);
    if (!minMaxThicknessEntry) {
      console.error("minMaxThickness not found!", width, this._minMaxThickness);
    }
    return minMaxThicknessEntry;
  };// /getMinMaxThickness()

  public getIdealThickness = (width:number):IIdealThickness => {
    const idealThicknessEntry = this._idealThickness.find(it => it.width === width);
    if (!idealThicknessEntry) {
      console.error("idealThickness not found!", width, this._idealThickness);
    } else if (!this.morphOptions.thickness.includes(idealThicknessEntry.ideal)) {
      console.error("idealThickness found, but not in available thickness options!", idealThicknessEntry.ideal);
    }
    return idealThicknessEntry;
  };// /getIdealThickness()
}